import React, { useState, useEffect } from "react";
import "../personal-info/personal-info.css";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaIdCard,
  FaFileUpload,
} from "react-icons/fa";
import {
  createPersonalInfo,
  fetchBasicCandidateInfo,
  getPersonalInfo,
  getEditRequestStatus,
} from "../../../services/candidate/api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getS3key } from "../../../services/employee/api";
import { sendNotificationToClient } from "../../../services/candidate/notificationapi";

const PersonalInfoForm = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    fatherName: "",
    dateOfBirth: "",
    email: "",
    gender: "Male",
    mobileNumber: "",
    alternateMobileNumber: "",
    aadhaarCardNumber: "",
    personalImage: null,
    personalImageFileName: "",
    imagePreview: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApiData, setHasApiData] = useState(false);
  const candidate_id = localStorage.getItem("user_name");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadImage = async (personalImage) => {
    try {
      let fileKey = personalImage;

      if (fileKey.includes(".com/")) {
        fileKey = fileKey.split(".com/")[1];
      }

      fileKey = decodeURIComponent(fileKey);

      const response = await getS3key(fileKey);

      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);

      const file = new File([blob], fileKey.split("/").pop(), {
        type: blob.type || "image/jpeg",
      });

      setFormData((prev) => ({
        ...prev,
        personalImage: file,
        personalImageFileName: file.name,
        imagePreview: blobUrl,
      }));
    } catch (error) { }
  };

  const fetchAndSetPersonalInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPersonalInfo();
      const apiData = response?.data?.data;

      if (!apiData) return;

      const updatedForm = {
        firstName: apiData.firstName || "",
        lastName: apiData.lastName || "",
        fatherName: apiData.fatherName || "",
        email: apiData.email || "",
        gender: apiData.gender || "Male",
        mobileNumber: apiData.mobileNumber || "",
        alternateMobileNumber: apiData.alternateMobileNumber || "",
        aadhaarCardNumber: apiData.aadhaarCardNumber || "",
        dateOfBirth: apiData.dateOfBirth
          ? (() => {
            const [d, m, y] = apiData.dateOfBirth.split("-");
            return `${y}-${m}-${d}`;
          })()
          : "",
      };

      setFormData((prev) => ({
        ...prev,
        ...updatedForm,
      }));

      if (apiData.personalImageFileName) {
        await loadImage(apiData.personalImageFileName);
      }
    } catch (err) {
      setError("Failed to load personal info");
    } finally {
      setLoading(false);
    }
  };

  const fetchBasicInfo = async () => {
    try {
      const response = await fetchBasicCandidateInfo();
      const data = response.data;

      if (!data) return;

      const payload = data.data;

      setFormData((prev) => ({
        ...prev,
        email: payload.email ?? prev.email,
        mobileNumber: payload.mobileNumber ?? prev.mobileNumber,
      }));
    } catch (error) { }
  };

  useEffect(() => {
    fetchAndSetPersonalInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (["firstName", "lastName"].includes(name)) {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
    if (name === "fatherName") {
      updatedValue = value.replace(/[^a-zA-Z\s/]/g, "");
    }
    if (["mobileNumber", "alternateMobileNumber"].includes(name)) {
      updatedValue = value.replace(/[^0-9]/g, "").slice(0, 10);
    }
    if (name === "aadhaarCardNumber") {
      updatedValue = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12);
    }

    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) {
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        personalImage: file,
        personalImageFileName: file.name,
        imagePreview: previewUrl,
      }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        personalImage: file,
        personalImageFileName: file.name,
        imagePreview: previewUrl,
      }));
    }
  };

  const validateFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPEG, PNG, or GIF).");
      return false;
    }
    if (file.size > maxSize) {
      toast.error("Image file size must be less than 5MB.");
      return false;
    }
    return true;
  };

  const validate = () => {
    const requiredFields = [
      "firstName",
      "dateOfBirth",
      "email",
      "gender",
      "mobileNumber",
      "aadhaarCardNumber",
    ];

    const missingFields = requiredFields.filter((field) => {
      const isFieldEmpty =
        !formData[field] || formData[field].toString().trim() === "";
      if (isFieldEmpty) {
      }
      return isFieldEmpty;
    });

    const isImagePresent = formData.personalImage || formData.imagePreview;
    if (!isImagePresent) {
      missingFields.push("Photo"); // User-friendly name for  toast.error
    }

    if (missingFields.length > 0) {
      toast.error(
        `Please fill the following required fields: ${missingFields.join(", ")}`,
      );
      return false;
    }

    // if (!/^[A-Za-z\s]{1,40}$/.test(formData.firstName)) {
    //    toast.error('First name must contain only letters and spaces, up to 40 characters.');
    //   return false;
    // }
    // if (!/^[A-Za-z\s]{1,40}$/.test(formData.lastName)) {
    //    toast.error('Last name must contain only letters and spaces, up to 40 characters.');
    //   return false;
    // }
    // if (!/^[A-Za-z\s/]{1,40}$/.test(formData.fatherName.trim())) {
    //    toast.error('Father/Spouse name can contain only alphabets, spaces, and \'/\' (max 40 characters).');
    //   return false;
    // }

    if (!/^\d{10}$/.test(formData.mobileNumber)) {
      toast.error("Mobile number must be exactly 10 digits.");
      return false;
    }
    if (
      formData.alternateMobileNumber &&
      !/^\d{10}$/.test(formData.alternateMobileNumber)
    ) {
      toast.error(
        "Alternate mobile number must be exactly 10 digits if provided.",
      );
      return false;
    }
    if (!/^[A-Za-z0-9]{12}$/.test(formData.aadhaarCardNumber)) {
      toast.error("Aadhaar number must be exactly 12 alphanumeric characters.");
      return false;
    }

    if (!["Male", "Female", "Other"].includes(formData.gender)) {
      toast.error("Please select a valid gender.");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      toast.error("Invalid email format.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append("firstName", formData.firstName);
      submitData.append("lastName", formData.lastName);
      submitData.append("fatherName", formData.fatherName);
      if (formData.dateOfBirth) {
        const [year, month, day] = formData.dateOfBirth.split("-");
        submitData.append("dateOfBirth", `${day}-${month}-${year}`);
      }
      submitData.append("email", formData.email);
      submitData.append("gender", formData.gender);
      submitData.append("mobileNumber", formData.mobileNumber);
      submitData.append(
        "alternateMobileNumber",
        formData.alternateMobileNumber || "",
      );
      submitData.append("aadhaarCardNumber", formData.aadhaarCardNumber);

      if (formData.personalImage) {
        submitData.append("personalImage", formData.personalImage);
      }

      for (let [key, value] of submitData.entries()) {
        if (value instanceof File) {
        } else {
        }
      }

      await createPersonalInfo(submitData);

      const editRequestResponse = await getEditRequestStatus();

      const requests = Array.isArray(editRequestResponse)
        ? editRequestResponse
        : [];

      const personalEditRequest = requests
        .filter(
          (request) =>
            request.requestType === "PERSONAL_DETAILS"
        )
        .sort((a, b) => {
          const dateA = a.createdAt
            ? new Date(a.createdAt).getTime()
            : 0;

          const dateB = b.createdAt
            ? new Date(b.createdAt).getTime()
            : 0;

          if (dateB !== dateA) {
            return dateB - dateA;
          }

          return Number(b.id || 0) - Number(a.id || 0);
        })[0];

      if (
        isEditMode &&
        personalEditRequest?.status === "APPROVED"
      ) {

        const EDIT_ORDER = [
          "PERSONAL_DETAILS",
          "ADDRESS_DETAILS",
          "EDUCATION_DETAILS",
          "EMPLOYMENT_DETAILS",
        ];

        const latestRequestByType = (requestType) => {
          return requests
            .filter(
              (request) =>
                request.requestType === requestType
            )
            .sort((a, b) => {
              const dateA = a.createdAt
                ? new Date(a.createdAt).getTime()
                : 0;

              const dateB = b.createdAt
                ? new Date(b.createdAt).getTime()
                : 0;

              if (dateB !== dateA) {
                return dateB - dateA;
              }

              return Number(b.id || 0) - Number(a.id || 0);
            })[0];
        };

        const nextApprovedRequest = EDIT_ORDER
          .filter(
            (requestType) =>
              requestType !== "PERSONAL_DETAILS"
          )
          .find((requestType) => {
            const latestRequest =
              latestRequestByType(requestType);

            return latestRequest?.status === "APPROVED";
          });

        toast.success(
          "Personal information updated successfully."
        );

        if (nextApprovedRequest === "ADDRESS_DETAILS") {
          navigate("/candidate/address-info");
          return;
        }

        if (nextApprovedRequest === "EDUCATION_DETAILS") {
          navigate("/candidate/education-info");
          return;
        }

        if (nextApprovedRequest === "EMPLOYMENT_DETAILS") {
          navigate("/candidate/employment-info");
          return;
        }

        navigate("/candidate/declaration");
        return;
      }

      const candidateNotification = {
        title: "Candidate Personal Information",
        message: `${candidate_id} has completed the Personal Information section.`,
        senderId: candidate_id,
        userRole: "CANDIDATE",
        receiverId: "",
        isRead: false,
        redirectUrl: "/candidate/personal-info",
      };

      await sendNotificationToClient(candidateNotification);

      toast.success(
        "Personal information saved successfully."
      );

      navigate("/candidate/address-info");
    } catch (error) {
      if (error.response) {
        toast.error(
          `Failed to save personal information: ${error.response.data.message || "Server error"}. Please try again.`,
        );
      } else {
        toast.error(
          "Failed to save personal information: Network error. Please check your connection and try again.",
        );
      }
    }
  };

  if (loading) {
    return (
      <div className="centered-message">
        <div className="spinner"></div>
        <div>Loading personal info...</div>
      </div>
    );
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  const getMaskedAadhaar = () => {
    const aadhaar = formData.aadhaarCardNumber;
    return aadhaar && aadhaar.length === 12
      ? "XXXXXXXX" + aadhaar.slice(-4)
      : aadhaar;
  };

  return (
    <div className="container">
      <div className="">
        <form className="form" onSubmit={handleSubmit}>
          <div className="personal-grid-2col">
            <div className="inputField full-width">
              <label>
                <FaFileUpload /> Photo*
              </label>
              <div
                className="dropzone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => document.getElementById("photoInput").click()}
              >
                {!formData.imagePreview && (
                  <p>Drag & drop your photo here or click to upload</p>
                )}
                {formData.imagePreview && (
                  <div className="image-preview">
                    <img src={formData.imagePreview} alt="Uploaded preview" />
                  </div>
                )}
                <input
                  type="file"
                  id="photoInput"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />
              </div>
            </div>

            <div className="inputField">
              <label>
                <FaUser /> First Name*
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                readOnly
                required
              />
            </div>
            <div className="inputField">
              <label>
                <FaUser /> Last Name*
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                readOnly
              />
            </div>
            <div className="inputField">
              <label>
                <FaUser /> Father's Name/Spouse Name*
              </label>
              <input
                type="text"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
                readOnly
              />
            </div>
            <div className="inputField">
              <label>
                <FaCalendarAlt /> DOB*
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                readOnly
                required
              />
            </div>
            <div className="inputField">
              <label>
                <FaEnvelope /> Email*
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="inputField">
              <label>
                <FaUser /> Gender*
              </label>
              <div className="radioGroupHorizontal">
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="Male"
                    checked={formData.gender === "Male"}
                    required
                  />
                  <span>Male</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="Female"
                    checked={formData.gender === "Female"}
                  />
                  <span>Female</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="Other"
                    checked={formData.gender === "Other"}
                  />
                  <span>Other</span>
                </label>
              </div>
            </div>

            <div className="inputField">
              <label>
                <FaPhone /> Mobile*
              </label>
              <input
                type="text"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                required
              />
            </div>
            <div className="inputField">
              <label>
                <FaPhone /> Alternate
              </label>
              <input
                type="text"
                name="alternateMobileNumber"
                value={formData.alternateMobileNumber}
                onChange={handleChange}
              />
            </div>
            <div className="inputField">
              <label>
                <FaIdCard /> Aadhaar No*
              </label>
              <input
                type="text"
                name="aadhaarCardNumber"
                value={getMaskedAadhaar()}
                readOnly
                required
              />
            </div>
          </div>
          <div className="submitSection">
            <button type="submit" className="submitBtn">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalInfoForm;
